package com.evoapps.evoshift.data.remote

import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.withTimeoutOrNull
import kotlin.time.Duration
import kotlin.time.Duration.Companion.minutes
import kotlin.time.Duration.Companion.seconds
import kotlin.time.TimeMark
import kotlin.time.TimeSource

/**
 * SH-185: Firebase App Check tokens for the requests the app makes itself (the callable functions
 * and Storage over REST, [CloudFunctionsClient], [FirebaseStorageClient]). Firestore and Auth get
 * theirs from the native SDK once App Check is installed on the Firebase app.
 *
 * [provider] is set by the platform that installed App Check (Android: AndroidAppCheck, iOS:
 * IosAppCheck through AppCheckBridge.swift, web: WebAppCheck); the Mac app and every emulator run
 * leave it empty, so no header is sent. Monitoring only (docs/app-check.md): a token that cannot be
 * had never fails or delays a call for long - after [TIMEOUT] or an error the call goes on without
 * one and no token is asked for during [BACKOFF].
 */
object AppCheckTokens {
    /** The request header Firebase reads the token from. */
    const val HEADER = "X-Firebase-AppCheck"

    val TIMEOUT: Duration = 3.seconds
    val BACKOFF: Duration = 5.minutes

    var provider: (suspend () -> String?)? = null
        set(value) {
            field = value
            pausedUntil = null
        }

    private var pausedUntil: TimeMark? = null

    /** The current token, or null (none installed, paused after a failure, or none in time). */
    suspend fun current(): String? {
        val tokens = provider ?: return null
        if (pausedUntil?.hasNotPassedNow() == true) return null
        val token = try {
            withTimeoutOrNull(TIMEOUT) { tokens() }
        } catch (cancelled: CancellationException) {
            throw cancelled
        } catch (_: Throwable) {
            null
        }
        if (token.isNullOrBlank()) {
            pausedUntil = TimeSource.Monotonic.markNow() + BACKOFF
            return null
        }
        return token
    }
}
